/* SDK pinned so privacy/queue behavior cannot change without another review. */
(() => {
  const element = document.getElementById('cloudnine-posthog-config');
  if (!element || window.CloudNinePostHog || window.posthog) return;
  let config;
  try { config = JSON.parse(element.textContent); } catch { return; }
  if (!/^phc_[\w-]+$/.test(config.token) || !['qa', 'production'].includes(config.environment)) return;
  if (/\/(account|checkouts?|orders|password)(\/|$)/i.test(location.pathname)) return;

  const host = 'https://us.i.posthog.com';
  const bridgeKey = `cn_ph_bridge_${config.environment}`;
  const withdrawalKey = `cn_ph_withdrawal_${config.environment}`;
  const domain = /(^|\.)cloudnineclothing\.ca$/.test(location.hostname) ? '; Domain=cloudnineclothing.ca' : '';
  const campaignKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  // A is the retained product-template filename, not an experiment arm.
  // Do not forward arbitrary or retired view names into new observations.
  function allowlistedView(value) {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim().slice(0, 60);
    if (/^grounding-layout-a$/i.test(trimmed)) return trimmed;
    return null;
  }
  const serverTemplateSuffix = typeof config.templateSuffix === 'string' ? allowlistedView(config.templateSuffix) : null;
  const urlView = (() => {
    try { return allowlistedView(new URL(location.href).searchParams.get('view')); } catch { return null; }
  })();
  // Staff devices: open the store once with ?cn_internal=1 to mark this browser internal (?cn_internal=0 undoes it).
  // Dashboards exclude is_internal, which survives IP changes.
  const internal = (() => {
    try {
      const flag = new URL(location.href).searchParams.get('cn_internal');
      if (flag === '1') localStorage.setItem('cn_internal', '1');
      if (flag === '0') localStorage.removeItem('cn_internal');
      return localStorage.getItem('cn_internal') === '1';
    } catch { return false; }
  })();
  const context = {
    schema_version: 2,
    source: 'cloudnine_theme',
    measurement_scope: 'theme_layout',
    environment: config.environment,
    store_id: config.shop,
    theme_id: String(config.themeId),
    theme_variant: typeof config.themeVariant === 'string' ? config.themeVariant : null,
    page_type: config.pageType,
    template_name: typeof config.templateName === 'string' ? config.templateName.slice(0, 60) : null,
    template_suffix: serverTemplateSuffix,
    view: serverTemplateSuffix || urlView,
    product_id: config.productId ? String(config.productId) : null,
    product_handle: config.productHandle,
    collection_id: config.collectionId ? String(config.collectionId) : null,
    collection_handle: config.collectionHandle,
    currency: config.currency,
    $process_person_profile: false,
    is_qa: config.environment === 'qa',
    is_internal: internal,
  };
  let phase = 'waiting_for_consent';
  let analyticsStarted = false;
  let sdk;
  let lastBridge = '';
  let lastBridgeAt = 0;
  function previewPage() {
    // Merchant previews of the published theme must not enter production cohorts.
    const params = new URLSearchParams(location.search);
    return config.previewMode || window.Shopify?.designMode ||
      params.has('preview_theme_id') || params.get('isShopliftMerchant') === 'true' ||
      params.has('previewConfig') || document.getElementById('shoplift-preview-control') ||
      // Staff theme previews: Shopify's preview bar data (the redirect drops ?preview_theme_id), or a tab that
      // snippets/theme-variant-tag.liquid has already marked as a preview.
      document.getElementById('OnlineStorePreviewBarNextData') ||
      (() => { try { return sessionStorage.getItem('cn_preview') === '1'; } catch { return false; } })();
  }

  function permitted() {
    return phase !== 'withdrawn' && !(config.environment === 'production' && previewPage()) &&
      window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  }

  function safeUrl(value) {
    if (!value || value === '(direct)' || value === '$direct') return value || '';
    try {
      const url = new URL(value, location.origin);
      if (!['https:', 'http:'].includes(url.protocol)) return '';
      const clean = new URL(url.origin + url.pathname);
      // Keep campaign grouping without forwarding click IDs, email or arbitrary query parameters.
      for (const key of campaignKeys) {
        const value = url.searchParams.get(key);
        if (value && /^[\w .+/-]{1,150}$/.test(value)) clean.searchParams.set(key, value);
      }
      // Preserve only the maintained Grounding template name.
      const view = allowlistedView(url.searchParams.get('view'));
      if (view) clean.searchParams.set('view', view);
      return clean.href;
    } catch { return ''; }
  }

  function sanitize(value, key = '') {
    if (Array.isArray(value)) return value.map(item => sanitize(item));
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value)
        .filter(([name]) => !['$set', '$set_once', 'email', 'phone', 'first_name', 'last_name'].includes(name))
        .map(([name, item]) => [name, sanitize(item, name)]));
    }
    if (typeof value === 'string' && /url|referrer|href/i.test(key)) return safeUrl(value);
    return value;
  }

  function assignments() {
    // Observation only, after consent; never infer assignment from the DOM.
    if (!permitted()) return [];
    // Ended tests can remain in visitor cookies. Do not carry those historical
    // assignments into new theme events or refresh them in the commerce bridge.
    // Other Shoplift tests keep their existing observation/consent behavior.
    const retired = new Set(['01a08b26-4540-7004-b4cb-6e3559833254', '6e029380-9d88-43da-a848-a3ae06b5bd92', '01a08cd9-8b57-7486-a627-da8d03493fdc', '01a0f1ce-fe72-71d1-974e-7ab248f943ee']);
    const normalize = tests => Array.isArray(tests) ? tests.filter(test => test && !test.isInvalid).map(test => ({
      test_id: String(test.testId || test.test_id || test.id || ''),
      hypothesis_id: String(test.hypothesisId || test.hypothesis_id || test.variantId || ''),
    })).filter(test => !retired.has(test.test_id) && /^[\da-f-]{36}$/i.test(test.test_id) && /^[\da-f-]{36}$/i.test(test.hypothesis_id)) : [];
    let api = [], cookie = [];
    try { api = normalize(window.shoplift?.getVisitorData?.()?.visitorTests); } catch { /* Fall back independently if the API throws. */ }
    try {
      const raw = document.cookie.split(';').map(part => part.trim()).find(part => part.startsWith('Shoplift_Essential='));
      if (raw && raw.length < 16384) {
        const data = JSON.parse(decodeURIComponent(raw.slice(raw.indexOf('=') + 1)).replace(/\\\\"/g, '"'));
        cookie = normalize(data.visitorTests);
      }
    } catch { /* Missing/malformed cookies remain unassigned, never control. */ }
    // Prefer current API evidence over a stale cookie for the same test.
    const observed = new Set(api.map(test => test.test_id));
    return [...api, ...cookie.filter(test => !observed.has(test.test_id))].slice(0, 10);
  }

  function bridge() {
    if (!sdk || !permitted() || sdk.has_opted_out_capturing()) return;
    const identity = {
      version: 1,
      project: config.token,
      environment: config.environment,
      theme_id: String(config.themeId),
      theme_variant: typeof config.themeVariant === 'string' ? config.themeVariant : null,
      distinct_id: sdk.get_distinct_id(),
      session_id: sdk.get_session_id(),
      shoplift_assignments: assignments(),
    };
    const signature = JSON.stringify(identity);
    if (signature === lastBridge && Date.now() - lastBridgeAt < 60000) return;
    lastBridge = signature;
    lastBridgeAt = identity.updated_at = Date.now();
    // Own the bridge instead of scraping Shopify/PostHog cookies. A longer purchase
    // window preserves the visitor, while the pixel expires the session separately.
    const lifetime = config.environment === 'qa' ? 1800 : 604800;
    document.cookie = `${bridgeKey}=${encodeURIComponent(JSON.stringify(identity))}; Path=/; Max-Age=${lifetime}; SameSite=Lax; Secure${domain}`;
  }

  let discovery, discoveryLoading = false, documentAway = false, discoveryGeneration = 0;

  function mountDiscovery() {
    if (discovery || discoveryLoading || documentAway || phase !== 'active' || !permitted() || !context.page_view_id) return;
    const generation = ++discoveryGeneration;
    discoveryLoading = true;
    import(config.discoveryModule).then(module => {
      // Navigation/withdrawal can happen during download. BFCache keeps a mounted
      // controller, but an unmounted stale import must not attach to an away page.
      if (generation !== discoveryGeneration || documentAway || phase !== 'active' || !permitted()) return;
      const options = { capture, permitted, config, getPageViewId: () => context.page_view_id };
      discovery = module.default(options);
    }).catch(() => { /* Base analytics and shopping survive module/download failure. */ })
      .finally(() => { if (generation === discoveryGeneration) discoveryLoading = false; });
  }

  function capture(name, properties = {}) {
    if (!sdk || phase !== 'active' || !permitted()) return false;
    try { sdk.capture(name, properties); return true; } catch { return false; }
  }

  function withdraw() {
    if (phase === 'withdrawn') return;
    const wasStarted = phase !== 'waiting_for_consent';
    phase = 'withdrawn';
    ++discoveryGeneration;
    try { discovery?.stop(); } catch { /* Continue SDK queue quarantine even if teardown fails. */ }
    document.cookie = `${bridgeKey}=; Path=/; Max-Age=0; SameSite=Lax; Secure${domain}`;
    if (!wasStarted) return;
    try { localStorage.setItem(withdrawalKey, String(Date.now())); } catch { /* Storage can be blocked. */ }
    if (!sdk) return;
    sdk.opt_out_capturing();
    // Pinned SDK 1.434.11 does not discard all queued requests on opt-out alone.
    // Its shared request dispatcher checks __loaded before every send/retry, even
    // in the minified CDN build. Quarantine until navigation; never re-enable this
    // instance or call shutdown(), which normally flushes buffered events.
    sdk.__loaded = false;
    sdk.set_config({ autocapture: false, advanced_disable_flags: true, disable_session_recording: true });
  }

  function activateAnalytics() {
    if (!sdk || analyticsStarted || !permitted()) return;
    analyticsStarted = true;
    phase = 'active';
    sdk.opt_in_capturing({captureEventName: false});
    sdk.set_config({capture_pageleave: true, disable_session_recording: !config.replay});
    try { context.page_view_id = crypto.randomUUID(); } catch { /* No weaker identity fallback. */ }
    for (const key of campaignKeys) {
      const value = new URL(location.href).searchParams.get(key);
      if (value && /^[\w .+/-]{1,150}$/.test(value)) context[key] = value;
    }
    sdk.onSessionId(() => { lastBridge = ''; bridge(); });
    bridge();
    // Product and collection views are $pageview filtered by page_type.
    capture('$pageview');
    mountDiscovery();
  }

  function start() {
    if (!permitted()) return;
    if (phase === 'flags_only') { activateAnalytics(); return; }
    if (phase !== 'waiting_for_consent') return;
    phase = 'loading_sdk';
    const script = document.createElement('script');
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.src = 'https://us-assets.i.posthog.com/static/1.434.11/array.js';
    script.onerror = () => { if (phase !== 'withdrawn') phase = 'sdk_unavailable'; };
    script.onload = () => {
      // No init queue: consent is checked again AFTER the download, before SDK storage/config requests.
      if (!permitted() || phase !== 'loading_sdk') return;
      sdk = window.posthog;
      if (typeof sdk?.init !== 'function') { phase = 'sdk_unavailable'; return; }
      if (sdk.__loaded) { sdk = null; phase = 'sdk_conflict'; return; }
      sdk.init(config.token, {
        api_host: host,
        ui_host: 'https://us.posthog.com',
        defaults: '2026-05-30',
        strict_script_versioning: true,
        person_profiles: 'never',
        persistence_name: `cn_ph_${config.environment}`,
        opt_out_capturing_by_default: true,
        opt_out_persistence_by_default: false,
        capture_pageview: false,
        capture_pageleave: false,
        // Autocapture stays off; masked click text left it unreadable.
        autocapture: false,
        // Autocapture event privacy is separate from replay visibility.
        mask_all_text: true,
        mask_all_element_attributes: true,
        disable_capture_url_hashes: true,
        mask_personal_data_properties: true,
        save_campaign_params: false,
        capture_exceptions: false,
        capture_heatmaps: false,
        capture_dead_clicks: false,
        // Real-user Core Web Vitals (LCP, INP, CLS, FCP) only; no network timing.
        capture_performance: {web_vitals: true, network_timing: false},
        rageclick: false,
        advanced_disable_feature_flags: true,
        advanced_disable_feature_flags_on_first_load: true,
        disable_web_experiments: true,
        disable_surveys: true,
        disable_conversations: true,
        disable_product_tours: true,
        opt_in_site_apps: false,
        disable_session_recording: true,
        enable_recording_console_log: false,
        session_recording: {
          // Show storefront content, but never record arbitrary customer input.
          // Checkout/account/orders never load this script.
          // Keep QA deterministic and production sampling at the approved 10%.
          sampleRate: config.environment === 'qa' ? 1 : 0.1,
          maskAllInputs: true,
          maskInputFn: (text, element) => {
            // Mask free text from the first keystroke, including search, names,
            // notes and revealed passwords. Regex cannot protect partial input.
            try {
              if (element?.matches('select[name="id"], select[name^="options["], input[type="radio"], input[type="checkbox"], input[type="number"][name="quantity"], input[type="number"][name="updates[]"]')) return text;
            } catch { /* Unknown elements stay masked. */ }
            return '*'.repeat(String(text || '').length);
          },
          maskTextSelector: '*',
          maskTextFn: (text, element) => {
            const value = String(text || '');
            try {
              // Reflected search terms, live-region copies and initial textarea
              // text need the same protection as the inputs that produced them.
              if (config.pageType === 'search' && element?.closest('title')) return 'Search';
              if (element?.closest('textarea, .ph-mask, [data-ph-mask], .predictive-search-status')) return '*'.repeat(value.length);
              return value
                .replace(/\S+@\S*/g, match => '*'.repeat(match.length))
                // Redact local/international phone-like runs, not prices or quantities.
                // This is a fallback for rendered text, not protection for free-text inputs.
                .replace(/\+?\d[\d().\s-]{5,}\d/g, match => (match.match(/\d/g) || []).length >= 7 ? '*'.repeat(match.length) : match);
            } catch { return '*'.repeat(value.length); }
          },
          // Kaching also embeds the entire cart, including private properties,
          // in a DOM attribute. Keep its visual drawer, not that backing JSON.
          maskAttributeFn: (name, value) => name === 'kaching-cart-props' ? '[redacted]' : value,
          // Kaching renders its own copy of cart properties outside theme markup.
          blockSelector: 'iframe, .ph-no-capture, [data-ph-no-capture], .kaching-cart-item__property-option, [contenteditable]:not([contenteditable="false"])',
          recordCrossOriginIframes: false,
          recordHeaders: false,
          recordBody: false,
          streamNetworkBody: false,
          captureJsonLd: false,
          captureCanvas: { recordCanvas: false },
          maskCapturedNetworkRequestFn: request => ({ ...request, name: safeUrl(request.name) }),
        },
        prepare_external_dependency_script: script => permitted() ? script : null,
        before_send: event => {
          if (!permitted() || phase !== 'active') return null;
          bridge();
          event.properties = sanitize({ ...event.properties, ...context, shoplift_assignments: assignments() });
          // Shopify includes the search query in the document title as well as q=.
          if (config.pageType === 'search') event.properties.$title = 'Search';
          return event;
        },
        loaded: instance => {
          sdk = instance;
          if (!permitted()) { withdraw(); return; }
          phase = 'flags_only';
          activateAnalytics();
        },
      });
    };
    document.head.appendChild(script);
  }

  // Community quiz: fixed event names and enum-only properties. Never quiz answers or free text.
  const communityQuizEvents = new Set(['community_quiz_viewed', 'community_quiz_started', 'community_quiz_step', 'community_quiz_completed', 'community_join_clicked']);
  const communityQuizProps = { flow: /^(self|parent)$/, mode: /^(quiz|join|app)$/, member_type: /^[a-z_]{3,30}$/, step: /^([0-9]|1[0-2])$/, platform: /^(ios|android|desktop)$/ };
  function communityQuiz(name, properties = {}) {
    if (!communityQuizEvents.has(name)) return false;
    const clean = {};
    for (const [key, pattern] of Object.entries(communityQuizProps)) {
      const value = properties[key];
      if (value != null && pattern.test(String(value))) clean[key] = String(value);
    }
    return capture(name, clean);
  }

  // Do not expose a raw capture API accepting arbitrary payloads or customer text.
  window.CloudNinePostHog = Object.freeze({ status: () => phase, communityQuiz });
  document.addEventListener('visitorConsentCollected', () => {
    if (analyticsStarted && !permitted()) withdraw();
    else if (permitted()) start();
    else if (phase !== 'waiting_for_consent') withdraw();
  });
  window.addEventListener('storage', event => { if (event.key === withdrawalKey) withdraw(); });
  window.addEventListener('pagehide', () => {
    documentAway = true;
    ++discoveryGeneration;
    discoveryLoading = false;
  });
  window.addEventListener('pageshow', () => {
    documentAway = false;
    if (!permitted() && phase === 'active') withdraw();
    else mountDiscovery();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      if (!permitted() && phase === 'active') withdraw();
      else { lastBridge = ''; bridge(); }
    }
  });
  // This refresh is bounded by visible, consented activity, not a background polling loop.
  document.addEventListener('pointerdown', bridge, { passive: true });
  document.addEventListener('keydown', bridge, { passive: true });

  if (window.Shopify?.customerPrivacy) start();
  else window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], error => {
    if (!error) start();
  });
})();
